import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import MediaCard from '@/components/ChatInput/MediaCard';
import MediaCardLarge from '@/components/ChatInput/MediaCardLarge';
import { isImageArtifact } from '@/lib/imageArtifact';
import { groupMessageFiles } from '@/constants/utils/message_file_groups';

const fileKey = (file) => file.id || file._id || `${file.name || 'file'}-${file.lastModified || ''}`;

const createStyles = (align, isSmall) => StyleSheet.create({
  stack: {
    marginTop: isSmall ? 5 : 10,
    paddingBottom: isSmall ? 8 : 10,
    gap: isSmall ? 8 : 10,
    width: '100%',
    maxWidth: isSmall ? undefined : 560,
    alignSelf: align === 'right' ? 'flex-end' : 'flex-start',
    alignItems: align === 'right' ? 'flex-end' : 'stretch',
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
  },
  docCell: {
    flexGrow: 1,
    flexBasis: isSmall ? undefined : 220,
    minWidth: isSmall ? 200 : 180,
    maxWidth: '100%',
  },
  mediaCell: {
    flexGrow: 1,
    flexBasis: 160,
    minWidth: 160,
    maxWidth: '100%',
    aspectRatio: 1,
  },
  mediaCellSolo: {
    width: '100%',
    maxWidth: 360,
    aspectRatio: 1,
  },
  // Match legacy image attachments without reserving a growing square cell.
  uploadMediaCell: {
    width: 140,
    height: 140,
    flexGrow: 0,
    flexShrink: 0,
  },
  uploadImageCell: {
    width: 80,
    height: 80,
    flexGrow: 0,
    flexShrink: 0,
  },
  audioRow: {
    width: '100%',
    minHeight: isSmall ? 56 : 108,
  },
});

const MessageFiles = ({ files, onPress, onDownload, align, isSmall, isEditable, onEdit }) => {
  const styles = useMemo(() => createStyles(align, isSmall), [align, isSmall]);
  const [showAllVisuals, setShowAllVisuals] = useState(false);
  const [showAllAudio, setShowAllAudio] = useState(false);

  const renderableFiles = useMemo(
    () => (Array.isArray(files) ? files.filter((file) => file && typeof file === 'object' && (file.cdnPath || file.name || file.mimeType)) : []),
    [files],
  );
  const grouped = useMemo(() => groupMessageFiles(renderableFiles), [renderableFiles]);
  const canCollapseVisuals = !isSmall && grouped.visuals.length > 6;
  const visibleVisuals = canCollapseVisuals && !showAllVisuals ? grouped.visuals.slice(0, 6) : grouped.visuals;
  const canCollapseAudio = !isSmall && grouped.audio.length > 3;
  // Keep the newest exports (usually the assembled track) close to the reply
  // instead of mounting dozens of raw scene players before its review card.
  const visibleAudio = canCollapseAudio && !showAllAudio ? grouped.audio.slice(-3) : grouped.audio;

  if (!renderableFiles.length) return null;

  const Component = isSmall ? MediaCard : MediaCardLarge;
  isEditable = false; // TODO: Enable this when the file editing is implemented

  const shared = {
    onPress,
    ...(onDownload ? { onDownload } : {}),
    onEdit,
    isEditable,
    isDownloadable: !isSmall,
  };

  return (
    <View style={styles.stack}>
      {grouped.documents.length > 0 && (
        <View style={styles.row}>
          {grouped.documents.map((file) => (
            <View key={fileKey(file)} style={isSmall ? undefined : styles.docCell}>
              <Component
                file={file}
                fillContainer={!isSmall}
                compact={!isSmall}
                {...shared}
              />
            </View>
          ))}
        </View>
      )}
      {grouped.visuals.length > 0 && (
        <View style={styles.row}>
          {visibleVisuals.map((file) => (
            <View
              key={fileKey(file)}
              style={isSmall ? (isImageArtifact(file) ? styles.uploadImageCell : styles.uploadMediaCell) : grouped.visuals.length === 1 ? styles.mediaCellSolo : styles.mediaCell}
            >
              <Component
                file={file}
                fillContainer={!isSmall}
                mediaSize={isSmall && isImageArtifact(file) ? 80 : undefined}
                isLarge={!isSmall && grouped.visuals.length === 1 && grouped.documents.length === 0 && grouped.audio.length === 0}
                {...shared}
              />
            </View>
          ))}
        </View>
      )}
      {canCollapseVisuals && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={showAllVisuals ? 'Show fewer media files' : `Show all ${grouped.visuals.length} media files`}
          accessibilityState={{ expanded: showAllVisuals }}
          aria-expanded={showAllVisuals}
          onPress={() => setShowAllVisuals((value) => !value)}
          style={{ paddingVertical: 8 }}
        >
          <Text size="xs" color="secondary">
            {showAllVisuals ? 'Show fewer media files' : `Show all ${grouped.visuals.length} media files`}
          </Text>
        </Pressable>
      )}
      {grouped.audio.length > 0 && (
        <View style={styles.row}>
          {visibleAudio.map((file) => (
            <View key={fileKey(file)} style={styles.audioRow}>
              {!isSmall && !!file.name && (
                <Text size="xs" color="secondary" numberOfLines={2} style={{ marginBottom: 4 }}>
                  {file.name}
                </Text>
              )}
              <Component
                file={file}
                fillContainer={!isSmall}
                isLarge={false}
                {...shared}
              />
            </View>
          ))}
        </View>
      )}
      {canCollapseAudio && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={showAllAudio ? 'Show fewer audio files' : `Show all ${grouped.audio.length} audio files`}
          accessibilityState={{ expanded: showAllAudio }}
          aria-expanded={showAllAudio}
          onPress={() => setShowAllAudio((value) => !value)}
          style={{ paddingVertical: 8 }}
        >
          <Text size="xs" color="secondary">
            {showAllAudio ? 'Show fewer audio files' : `Show all ${grouped.audio.length} audio files`}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

export default MessageFiles;
